import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import toast from 'react-hot-toast';
import styles from './JobPreferences.module.css';
import { useFormik } from 'formik';
import { t } from 'i18next';

import Button from '../../Buttons/Button/Button';
import FormSelectField from '../../FormUI/FormSelectField/FormSelectField';
import FormField from '../../FormUI/FormField/FormField'; // Replace with correct path
import BubbleButton from '../../Buttons/BubbleButton/BubbleButton';
import { Close } from '@mui/icons-material';
import { EditIcon } from '../../../../assets/images/icons'; // Replace with correct import
import { updateUser } from '../../../../services/userService';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrencySymbolByCountry } from '../../../../utils/formatHelpers';
import { fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import { useGetRoleBasedSkillsHook } from '../../../../utils/utilityHooks';

function JobPreferences({ jobseeker }) {
    const [editMode, setEditMode] = useState(false);
    const [formLoading, setFormLoading] = useState(false);

    // Job title suggestions
    const [showSuggestions, setShowSuggestions] = useState([false, false, false]);
    const [titleSuggestions, setTitleSuggestions] = useState([[], [], []]);
    const [titleInputs, setTitleInputs] = useState(['', '', '']);
    const jobTitleOptions = useGetRoleBasedSkillsHook();

    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token);
    const user = useSelector(state => state.user.user);
    const preferences = jobseeker?.jobPreferences[0] || {};
    const formik = useFormik({
        initialValues: {
            jobType: preferences.jobType ? JSON.parse(preferences.jobType) : [],
            basePay: preferences.basePay || "",
            payPeriod: preferences.payPeriod || "",
            relocation: preferences.relocation || "",
            jobTitles: preferences.jobTitles || [""],
        },
        validationSchema: Yup.object({
            jobType: Yup.array().of(Yup.string()).required(t('validation.jobTypeRequired')),
            basePay: Yup.number()
                .min(0, t('validation.basePayNegative'))
                .required(t('validation.basePayRequired')),
            payPeriod: Yup.string().required(t('validation.payPeriodRequired')),
            relocation: Yup.string().required(t('validation.relocationRequired')),
            jobTitles: Yup.array().of(Yup.string().max(50, t('validation.jobTitleTooLong')))
        }),
        onSubmit: (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);
            const formData = new FormData();
            formData.append('token', userToken);

            if ([0].length > 0) {
                [0].forEach((jobPreference, index) => {
                    if (Object.keys(values).length > 0) {
                        formData.append(`jobPreferences[${index}][basePay]`, values.basePay);
                        formData.append(`jobPreferences[${index}][jobTitles]`, JSON.stringify(values.jobTitles));
                        formData.append(`jobPreferences[${index}][jobType]`, JSON.stringify(values.jobType)); // Send as JSON array
                        formData.append(`jobPreferences[${index}][payPeriod]`, values.payPeriod);
                        formData.append(`jobPreferences[${index}][relocation]`, values.relocation);
                        if (jobPreference?.endDate) {
                            formData.append(`jobPreferences[${index}][end]`, values.endDate);
                        }
                    } else {
                        formData.append('workExperience', '');
                    }
                });
            }
            else {
                formData.append('workExperience', '');
            }

            // Check if any job titles need to be created
            // Instead of sending multiple create_new_title flags, we'll create a unique working_title parameter for each new title
            let newTitleCount = 0;
            values.jobTitles.forEach((title, index) => {
                if (title && title.trim()) {
                    const titleExists = jobTitleOptions.some(option =>
                        option.label?.toLowerCase() === title.toLowerCase()
                    );

                    if (!titleExists) {
                        // Use a numbered parameter name to handle multiple new titles
                        formData.append(`create_new_title_${newTitleCount}`, 'true');
                        formData.append(`working_title_${newTitleCount}`, title);
                        newTitleCount++;
                    }
                }
            });

            // Add the count of new titles
            if (newTitleCount > 0) {
                formData.append('new_title_count', newTitleCount);
            }

            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => <b>{err.response.data.error}</b>,
            }).then(async () => {
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                await setEditMode(!editMode)
                await setFormLoading(false);
                const target = document.querySelector('.paginationController');
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop,
                        behavior: 'smooth'
                    });
                }
            })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });
        },
    });

    const handleAddJobTitle = () => {
        const { jobTitles } = formik.values;
        if (jobTitles.length < 3) {
            formik.setFieldValue("jobTitles", [...jobTitles, ""]);
            setShowSuggestions([...showSuggestions, false]);
            setTitleSuggestions([...titleSuggestions, []]);
            setTitleInputs([...titleInputs, '']);
        }
    };

    const handleRemoveJobTitle = (id) => {
        formik.values.jobTitles.splice(id, 1);
        formik.setFieldValue("jobTitles", formik.values.jobTitles);

        const newShowSuggestions = [...showSuggestions];
        newShowSuggestions.splice(id, 1);
        setShowSuggestions(newShowSuggestions);

        const newTitleSuggestions = [...titleSuggestions];
        newTitleSuggestions.splice(id, 1);
        setTitleSuggestions(newTitleSuggestions);

        const newTitleInputs = [...titleInputs];
        newTitleInputs.splice(id, 1);
        setTitleInputs(newTitleInputs);
    };

    // Handle title input change and show suggestions
    const handleJobTitleChange = (index, value) => {
        const updatedTitles = [...formik.values.jobTitles];
        updatedTitles[index] = value;
        formik.setFieldValue("jobTitles", updatedTitles);

        // Update titleInputs
        const newTitleInputs = [...titleInputs];
        newTitleInputs[index] = value;
        setTitleInputs(newTitleInputs);

        if (value.trim()) {
            // Filter job title options that match the input
            const filteredOptions = jobTitleOptions.filter(option =>
                option.label?.toLowerCase().includes(value.toLowerCase())
            );

            // Update suggestions for this specific index
            const newTitleSuggestions = [...titleSuggestions];
            newTitleSuggestions[index] = filteredOptions.slice(0, 5); // Limit to top 5 matches
            setTitleSuggestions(newTitleSuggestions);

            // Show suggestions for this field
            const newShowSuggestions = [...showSuggestions];
            newShowSuggestions[index] = true;
            setShowSuggestions(newShowSuggestions);
        } else {
            // Hide suggestions if field is empty
            const newShowSuggestions = [...showSuggestions];
            newShowSuggestions[index] = false;
            setShowSuggestions(newShowSuggestions);
        }
    };

    // Handle selecting a title from suggestions
    const handleSelectTitle = (index, title) => {
        const updatedTitles = [...formik.values.jobTitles];
        updatedTitles[index] = title.label;
        formik.setFieldValue("jobTitles", updatedTitles);

        const newTitleInputs = [...titleInputs];
        newTitleInputs[index] = title.label;
        setTitleInputs(newTitleInputs);

        const newShowSuggestions = [...showSuggestions];
        newShowSuggestions[index] = false;
        setShowSuggestions(newShowSuggestions);
    };

    const jobTypeOptions = [
        { value: "Full-time", label: t('jobPreferences.jobTypeSection.options.fullTime') },
        { value: "Part-time", label: t('jobPreferences.jobTypeSection.options.partTime') },
        { value: "Temporary", label: t('jobPreferences.jobTypeSection.options.temporary') },
        { value: "Contract", label: t('jobPreferences.jobTypeSection.options.contract') },
        { value: "Internship", label: t('jobPreferences.jobTypeSection.options.internship') },
    ]
    useEffect(() => {
        if (preferences.jobType) {
            formik.setFieldValue("jobType", JSON.parse(preferences.jobType));
        }
        if (preferences.basePay) {
            formik.setFieldValue("basePay", preferences.basePay);
        }
        if (preferences.payPeriod) {
            formik.setFieldValue("payPeriod", preferences.payPeriod);
        }
        if (preferences.relocation) {
            formik.setFieldValue("relocation", preferences.relocation);
        }
        if (preferences.jobTitles) {
            formik.setFieldValue("jobTitles", preferences.jobTitles);
            setTitleInputs(preferences.jobTitles.map(title => title || ''));
        }
    }, [preferences])
    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.title}>{t(`profileSidebar.tabs.jobPreferences`)}</div>
                {editMode ?
                    <Close
                        className={styles.crossIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            setEditMode(!editMode)
                        }}
                    /> :

                    <img
                        src={EditIcon}
                        onClick={() => {
                            setEditMode(!editMode)
                        }}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                }
            </div>
            <form onSubmit={formik.handleSubmit} className='paginationController'>
                {/* Job Titles Section */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>{t('jobPreferences.jobTitlesSection.title')}</div>
                    {editMode ? (
                        <>
                            {formik.values.jobTitles.map((title, index) => (
                                <div key={index.toString()} style={{ display: "flex", width: '100%', }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <FormField
                                            key={index}
                                            name={`jobTitles[${index}]`}
                                            type="text"
                                            placeholder={t('jobPreferences.jobTitlesSection.placeholder')}
                                            hasBorder
                                            onChange={(e) => handleJobTitleChange(index, e.target.value)}
                                            value={titleInputs[index] || title}
                                            onBlur={() => {
                                                // Keep suggestions open for a moment to allow clicking
                                                setTimeout(() => {
                                                    const newShowSuggestions = [...showSuggestions];
                                                    newShowSuggestions[index] = false;
                                                    setShowSuggestions(newShowSuggestions);
                                                }, 200);
                                            }}
                                            error={
                                                formik.errors.jobTitles &&
                                                formik.errors.jobTitles[index]
                                            }
                                        />
                                        {showSuggestions[index] && titleSuggestions[index]?.length > 0 && (
                                            <div className={styles.suggestionsList}>
                                                {titleSuggestions[index].map(title => (
                                                    <div
                                                        key={title.id}
                                                        className={styles.suggestionItem}
                                                        onClick={() => handleSelectTitle(index, title)}
                                                    >
                                                        {title.label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.inputClose}>
                                        <Close
                                            className={styles.crossIcon}
                                            style={{
                                                cursor: "pointer",
                                            }}
                                            onClick={() => handleRemoveJobTitle(index)}
                                        />
                                    </div>

                                </div>
                            ))}
                            {formik.values.jobTitles.length < 3 && (
                                <BubbleButton
                                    dark
                                    headerDarkButton
                                    onClick={handleAddJobTitle}
                                    className={styles.addButton}
                                >
                                    {t('jobPreferences.jobTitlesSection.addJobTitle')}
                                </BubbleButton>
                            )}
                        </>
                    ) : (
                        <div className={styles.textDisplay}>
                            {preferences?.jobTitles?.length > 0 && preferences?.jobTitles[0]?.length == 0 ?
                                t('jobPreferences.jobTitlesSection.notSpecified') :
                                formik?.values?.jobTitles?.length > 0 && formik.values.jobTitles.filter(Boolean).map((title, index) => (
                                    <span key={index} className={styles.tag}>
                                        {title.length == 0 ? t('jobPreferences.jobTitlesSection.notSpecified') : title}
                                    </span>
                                )) || t('jobPreferences.jobTitlesSection.notSpecified')}
                        </div>

                    )}
                </div>

                {/* Job Type Section */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>{t('jobPreferences.jobTypeSection.title')}</div>
                    {editMode ? (
                        <FormSelectField
                            name="jobType"
                            type="default"
                            placeholder={t('jobPreferences.jobTypeSection.placeholder')}
                            hasBorder
                            options={jobTypeOptions}
                            value={formik.values.jobType}
                            isMulti
                            onFormikChange={formik.handleChange}
                            error={formik.touched.jobType && formik.errors.jobType}
                        />
                    ) : (
                        <div className={styles.textDisplay}>
                            {formik.values.jobType && formik.values.jobType.length > 0
                                ? formik.values.jobType.join(', ') // Display as comma-separated values
                                : t('jobPreferences.jobTitlesSection.notSpecified')}
                        </div>
                    )}
                </div>

                {/* Minimum Base Pay Section */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>{t('jobPreferences.basePaySection.title')}</div>
                    {editMode ? (
                        <>
                            <div style={{ position: "relative", display: 'flex', flexDirection: "column", justifyContent: "center" }}>
                                <FormField
                                    name="basePay"
                                    // type="number"
                                    placeholder={t('jobPreferences.basePaySection.placeholder')}
                                    hasBorder
                                    onChange={formik.handleChange}
                                    value={formik.values.basePay}
                                    error={formik.touched.basePay && formik.errors.basePay}
                                    style={{ paddingLeft: `50px` }}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        width: `50px`,
                                        display: "flex",
                                        justifyContent: 'center',
                                        transform: "translateY(-50%)",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {getCurrencySymbolByCountry('AS')}
                                </span>
                            </div>
                            <FormSelectField
                                name="payPeriod"
                                type="default"
                                placeholder={t('jobPreferences.basePaySection.payPeriodPlaceholder')}
                                hasBorder
                                options={[
                                    { value: "Per hour", label: t('jobPreferences.basePaySection.payPeriodOptions.perHour') },
                                    { value: "Per day", label: t('jobPreferences.basePaySection.payPeriodOptions.perDay') },
                                    { value: "Per week", label: t('jobPreferences.basePaySection.payPeriodOptions.perWeek') },
                                    { value: "Per month", label: t('jobPreferences.basePaySection.payPeriodOptions.perMonth') },
                                    { value: "Per year", label: t('jobPreferences.basePaySection.payPeriodOptions.perYear') },
                                ]}
                                value={formik.values.payPeriod}
                                onFormikChange={formik.handleChange}
                                error={formik.touched.payPeriod && formik.errors.payPeriod}
                            />
                        </>
                    ) : (
                        <div className={styles.textDisplay}>
                            {formik.values.basePay
                                ? `${formik.values.basePay} (${formik.values.payPeriod})`
                                : t('jobPreferences.jobTitlesSection.notSpecified')}
                        </div>
                    )}
                </div>

                {/* Relocation Section */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>{t('jobPreferences.relocationSection.title')}</div>
                    {editMode ? (
                        <FormSelectField
                            name="relocation"
                            type="default"
                            placeholder={t('jobPreferences.relocationSection.placeholder')}
                            hasBorder
                            options={[
                                { value: "Yes", label: t('jobPreferences.relocationSection.options.yes') },
                                { value: "No", label: t('jobPreferences.relocationSection.options.no') },
                            ]}
                            value={formik.values.relocation}
                            onFormikChange={formik.handleChange}
                            error={formik.touched.relocation && formik.errors.relocation}
                        />
                    ) : (
                        <div className={styles.textDisplay}>{formik.values.relocation || t('jobPreferences.jobTitlesSection.notSpecified')}</div>
                    )}
                </div>

                {editMode && (
                    <div className={styles.saveButtonContainer}>
                        <Button onClick={formik.submitForm} className={styles.saveButton} >
                            {t('jobPreferences.saveButton')}
                        </Button>
                    </div>
                )}
            </form>
        </div>
    );
}

export default JobPreferences;
